import { MouseEvent, useCallback, useRef, useState } from 'react'
import classNames from 'classnames'
import { PlusIcon } from '../../icons'
import ShopTheLookDrawer, { ShopTheLookProduct } from '../../shop-the-look-drawer/components/ShopTheLookDrawer'

type Props = {
  mount: HTMLElement
}

type LookMedia = {
  image?: string
  imageSrcset?: string
  imageAlt?: string
  video?: string
  videoPoster?: string
}

const parseJson = <T,>(raw: string | undefined, fallback: T): T => {
  try {
    return raw ? JSON.parse(raw) : fallback
  } catch (err) {
    console.error('Brand-Led Shop The Look: could not parse block data', err)
    return fallback
  }
}

const BrandLedShopTheLook = ({ mount }: Props) => {
  const dataset = mount.dataset
  const heading = dataset.heading || ''
  const description = dataset.description || ''
  const media = parseJson<LookMedia>(dataset.media, {})
  const products = parseJson<ShopTheLookProduct[]>(dataset.products, [])

  const [isOpen, setIsOpen] = useState(false)
  const toggleRef = useRef<HTMLButtonElement>(null)

  const hasProducts = products.length > 0

  /* Restore focus without scrolling to the toggle when it is outside the viewport. */
  const onClose = useCallback(() => {
    setIsOpen(false)
    toggleRef.current?.focus({ preventScroll: true })
  }, [])

  if (!media.image && !media.video) return null

  const onMediaClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!hasProducts) return
    if ((event.target as HTMLElement).closest('.ShopTheLookDrawer')) return

    if (isOpen) {
      onClose()
    } else {
      setIsOpen(true)
    }
  }

  const onToggleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    if (isOpen) {
      onClose()
    } else {
      setIsOpen(true)
    }
  }

  return (
    <div className={classNames('BrandLedShopTheLook', isOpen && 'is-open')}>
      <div className={classNames('BrandLedShopTheLook__Media', hasProducts && 'cursor-pointer')} onClick={onMediaClick}>
        {media.video ? (
          <video className="BrandLedShopTheLook__Asset" src={media.video} poster={media.videoPoster} autoPlay loop muted playsInline />
        ) : (
          <img
            className="BrandLedShopTheLook__Asset"
            src={media.image}
            srcSet={media.imageSrcset}
            sizes="(min-width: 768px) 25vw, 100vw"
            alt={media.imageAlt || ''}
            loading="lazy"
          />
        )}

        {(heading || description) && (
          <div className="BrandLedShopTheLook__Header">
            {heading && <h2 className="BrandLedShopTheLook__Heading font-bstSpyre font-light uppercase">{heading}</h2>}
            {description && <div className="BrandLedShopTheLook__Description font-calibre font-light" dangerouslySetInnerHTML={{ __html: description }} />}
          </div>
        )}

        {hasProducts && (
          <>
            <button
              ref={toggleRef}
              type="button"
              className="BrandLedShopTheLook__Toggle"
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Close shop the look' : 'Open shop the look'}
              onClick={onToggleClick}
            >
              <PlusIcon />
            </button>

            <ShopTheLookDrawer isOpen={isOpen} products={products} onClose={onClose} showClose={false} />
          </>
        )}
      </div>
    </div>
  )
}

export default BrandLedShopTheLook
